import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FlaskConicalIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, MicroscopeIcon, PencilRulerIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PRODUCT_IMAGE as BASE_PRODUCT_IMAGE, pageSamples as basePageSamples } from './content';
import { REAL_MOCKUP_URL, REAL_PAGE_URLS } from './realImages';

// ---------- Images (real URL if set, otherwise the original placeholder) ----------
export const PRODUCT_IMAGE = REAL_MOCKUP_URL || BASE_PRODUCT_IMAGE;
export const PRODUCT_IMAGE_ALT = 'NO KLAR-paketet: sex NO-volymer för åk 1–9 i biologi, fysik och kemi med lektioner, arbetsblad, experiment, prov och facit';

// ---------- Prices ----------
export const PRICE = '299 kr';
export const ORIGINAL_PRICE = '601 kr';

// ---------- Meta ----------
export const META = {
  title: 'NO KLAR™ – Färdiga NO-lektioner för åk 1–9 (biologi, fysik, kemi)',
  description: 'Färdigt 6-i-1-system för NO åk 1–9: 6 volymer, 240 sidor, 12 lektioner minut för minut, arbetsblad i 3 nivåer, 20 säkra experiment, prov A/B med facit och 5 bonusar. 299 kr.',
  ogTitle: 'NO KLAR™ – Gå in i NO-salen med allt klart',
  ogDescription: 'Ett helt läsårs NO-lektioner i biologi, fysik och kemi för åk 1–9 – lektioner, arbetsblad, experiment och prov med facit. Omedelbar nedladdning.'
};

// ---------- Section copy ----------
export const copy = {
  ctaDefault: 'Skaffa NO KLAR™ nu',
  hero: {
    before: 'Få',
    highlight: 'ett helt läsårs',
    middle: 'NO-lektioner klara på bara',
    accent: '5 minuter',
    sub: 'Sluta planera från grunden. Sex digitala volymer för åk 1–9 i biologi, fysik och kemi med färdiga lektioner, arbetsblad i tre nivåer, säkra experiment, prov och facit – plus fem bonusar.',
    badgeLine1: '6 volymer',
    badgeLine2: '+ 5 bonusar'
  },
  stats: { title: 'Det kompletta paketet i siffror' },
  difference: {
    kicker: 'Skillnaden med NO KLAR™',
    titleStart: 'Från stressad lärare till',
    titleAccent: 'trygg och förberedd.',
    intro: 'Tänk dig att börja veckan med NO-lektionerna redan organiserade. Du vet vad du ska undervisa om – och du har lektionsupplägget, arbetsbladen, experimenten, proven och facit.',
    beforeLabel: 'Innan NO KLAR™',
    beforeTitle: 'Känner du igen dig?',
    afterLabel: 'Med NO KLAR™',
    afterTitle: 'Planera mindre. Undervisa mer.',
    closing: 'Fokusera på dina elever – inte på att skapa allt material själv.'
  },
  recognize: {
    title: 'Har du någonsin upplevt det här?',
    text: 'Om du svarar ja på minst en fråga är NO KLAR™ för dig.',
    link: 'Du förtjänar ett enklare sätt'
  },
  inside: {
    title: 'Vad innehåller',
    brand: 'NO KLAR™',
    packTitle: 'Vad gör NO KLAR™ annorlunda?',
    packText: 'Ett helt läsårs NO-material – organiserat och redo att använda.'
  },
  pages: {
    kicker: 'Titta inuti NO KLAR™',
    titleStart: 'Se hur materialet ser ut',
    titleAccent: 'på insidan.',
    text: 'Exempel på sidor ur materialet: lärarsidor minut för minut, elevblad i tre nivåer, årsplanering, prov och bedömning – skapade för att användas i NO-salen.'
  },
  bonuses: {
    kicker: 'Men det finns mer!',
    titleStart: '5 bonusar',
    titleAccent: 'utan extra kostnad',
    text: 'Fem extra resurser ingår tillsammans med dina sex volymer – så att du kan organisera och genomföra NO-undervisningen ännu enklare.'
  },
  how: {
    titleStart: 'Så fungerar',
    brand: 'NO KLAR™',
    text: 'Från nedladdning till NO-salen: förbered nästa lektion på bara fem minuter.',
    closing: 'Öppna → välj → förbered → undervisa. Enkelt och strukturerat.'
  },
  audience: {
    titleStart: 'För vem är',
    brand: 'NO KLAR™',
    lead: 'Paketet är för dig som:',
    closing: 'Skapat för lärare som vill lägga mindre tid på förberedelser och mer tid på eleverna.',
    asideTitle: 'Vad är din tid värd?',
    asideLead: 'Tänk på hur många timmar du lägger på NO-planering varje vecka:',
    asideText: 'Tänk dig nu ett organiserat paket som hjälper dig med allt detta – under hela läsåret.',
    asideClosing: 'Du behöver inte fortsätta börja från noll.'
  },
  offer: {
    titleStart: 'Upptäck ditt kompletta',
    brand: 'NO KLAR™',
    titleEnd: '-paket',
    text: 'Få ett helt läsårs NO-lektioner klara på bara 5 minuter.',
    includesLabel: 'Det kompletta paketet innehåller',
    priceLabel: 'Introduktionspris',
    originalLabel: 'Ord. pris',
    oneTime: 'Engångsbetalning. Ingen prenumeration.',
    cta: 'Skaffa hela paketet nu'
  },
  guarantee: {
    title: '7 dagars nöjdhetsgaranti',
    text: 'Ladda ner materialet och se hur det fungerar i din NO-undervisning. Om paketet inte motsvarar dina förväntningar kan du kontakta oss via e-post och begära återbetalning inom 7 dagar från köpet, enligt våra garantivillkor.',
    legal: 'Garantin påverkar inte dina rättigheter enligt gällande konsumentlagstiftning.'
  },
  finalCta: {
    titleStart: 'Din NO-undervisning kan vara',
    titleAccent: 'redo redan idag.',
    text: 'I stället för att leta efter experiment, skapa varje arbetsblad själv och lägga kvällarna på att planera laborationer har du ett organiserat NO-paket för hela läsåret. Mer tid till det som verkligen betyder något:',
    closing: 'Dina elever. Din undervisning. Ditt liv utanför skolan.',
    recap: ['6 volymer', '5 bonusar', '240 sidor', 'Åk 1–9'],
    oneTime: 'Engångsbetalning – ingen prenumeration.',
    reassurances: ['Omedelbar digital nedladdning', 'Utskrivbara PDF-filer', '7 dagars nöjdhetsgaranti']
  },
  footer: {
    brand: 'NO KLAR',
    disclaimer: 'NO KLAR™ är ett fristående undervisningsmaterial i NO (biologi, fysik och kemi) för åk 1–9.',
    copyright: '© 2026 NO KLAR™ · edulabs. Alla rättigheter förbehållna.'
  }
};

// ---------- Data ----------
export const heroGuarantees = ['Omedelbar digital nedladdning', 'Engångsbetalning', 'Ingen prenumeration', '7 dagars nöjdhetsgaranti'];

export const stats = [{
  value: '240',
  label: 'Sidor i huvudvolymerna'
}, {
  value: '6',
  label: 'Digitala NO-volymer'
}, {
  value: '12',
  label: 'Lektioner minut för minut'
}, {
  value: '20',
  label: 'Säkra experiment med vardagsmaterial'
}, {
  value: '5',
  label: 'Bonusar som ingår'
}];

export const differenceStats = [{
  value: 'Åk 1–9',
  label: 'Årsplanering för åk 1–3, 4–6 och 7–9'
}, {
  value: '6 digitala volymer',
  label: 'Biologi, fysik, kemi, experiment och bedömning'
}, {
  value: 'Utskrivbara PDF-filer',
  label: 'Att använda digitalt eller skriva ut i klassrummet'
}];

export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Kvällarna försvinner',
  text: 'Timmar framför datorn för att planera nästa veckas NO-lektioner och laborationer – och du är fortfarande inte klar.'
}, {
  title: 'Timmar av letande efter experiment',
  text: 'Experiment från olika webbplatser kräver material du inte har, är för svåra eller känns inte tillräckligt säkra.'
}, {
  title: 'Arbetsbladen skapas från grunden',
  text: 'Frågor, bilder, uppställning och facit – det som skulle ta minuter blir en lång arbetsuppgift.'
}, {
  title: 'Tre ämnen att hålla ihop',
  text: 'Biologi, fysik och kemi – varje område kräver egna lektioner, egna arbetsblad och egna prov.'
}, {
  title: 'Prov och rättning tar tid',
  text: 'Prov för tre områden, facit och omdömen blir ännu mer arbete efter skoltid.'
}, {
  title: 'Stress inför nästa laboration',
  text: 'När tiden inte räcker till blir det lätt att improvisera och få ihop material i sista minuten.'
}];

export const afterItems = ['Ett organiserat NO-paket för hela grundskolan', 'Årsplanering för åk 1–3, 4–6 och 7–9', '12 lektioner planerade minut för minut', 'Arbetsblad i 3 nivåer som kan skrivas ut direkt', '20 säkra experiment med vardagsmaterial', 'Biologi, fysik och kemi samlat på ett ställe', 'Labbrapport-mallar och säkerhetsregler', 'Prov A/B med facit och bedömningsmatriser', '100 färdiga kommentarer till omdömen'];

export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Kvällarna går åt till planering',
  text: 'Sitter du på kvällar och helger och planerar nästa veckas NO-lektioner och laborationer?'
}, {
  icon: FolderSearchIcon,
  title: 'Du letar efter experiment som är säkra',
  text: 'Söker du ständigt efter experiment som fungerar i klassrummet – med material du faktiskt har?'
}, {
  icon: LayersIcon,
  title: 'Tre ämnen att planera',
  text: 'Ska du hålla ihop biologi, fysik och kemi – och skapa prov för alla tre områdena?'
}, {
  icon: PencilRulerIcon,
  title: 'Du vill slippa skapa allt från noll',
  text: 'Önskar du att lektioner, arbetsblad och experiment redan var förberedda?'
}, {
  icon: ShuffleIcon,
  title: 'Eleverna ligger på olika nivåer',
  text: 'Behöver du uppgifter som fungerar både för elever som behöver stöd och för dem som vill ha mer utmaning?'
}, {
  icon: LayoutGridIcon,
  title: 'Du behöver en tydligare struktur',
  text: 'Vill du ha materialet samlat så att du snabbt hittar det du behöver?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volym 01',
  level: 'Planering',
  title: 'Årsplanering + 12 lektioner minut för minut',
  tagline: 'Gå in i NO-salen med allt klart.',
  intro: 'Den första volymen ger dig strukturen för hela läsåret och tolv färdiga lektioner på 60 minuter – med lärarsida och elevblad till varje lektion.',
  items: ['Årsplanering för åk 1–3, 4–6 och 7–9', 'Planering vecka för vecka', '12-veckorsplan och progressionstabell', 'Säkerhet i NO-salen', '12 lektioner minut för minut (60 min)', 'Lärarsida + elevblad till varje lektion', 'Prov för åk 1–6 och åk 7–9', 'Facit (lärarexemplar)', 'Bedömningsmatris'],
  outro: 'Från ”Våra fem sinnen” i åk 1 till ”Elektriska kretsar” i åk 9 – färdigt att undervisa.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 02',
  level: 'Biologi',
  title: 'Kroppen, växter, djur & ekosystem',
  tagline: 'Hjälp eleverna att förstå livet omkring dem.',
  intro: 'Den andra volymen innehåller färdiga lektioner och arbetsblad i biologi för åk 1–9.',
  items: ['Kroppen', 'Växter', 'Djur', 'Ekosystem', 'Näringskedjor', 'Cellen', 'Evolution', 'Ekologi', 'Hälsa', 'Arbetsblad i 3 nivåer'],
  outro: 'Färdigt material som gör biologin konkret och begriplig.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 03',
  level: 'Fysik',
  title: 'Krafter, energi, ljus & elektricitet',
  tagline: 'Gör fysiken synlig och begriplig.',
  intro: 'Den tredje volymen innehåller färdiga lektioner och arbetsblad i fysik – från enkla undersökningar till mer avancerade samband.',
  items: ['Krafter', 'Rörelse', 'Energi', 'Ljus', 'Ljud', 'Elektricitet och magnetism', 'Rymden', 'Väder', 'Arbetsblad i 3 nivåer'],
  outro: 'Tydliga exempel och aktiviteter för fysikundervisningen i åk 1–9.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 04',
  level: 'Kemi',
  title: 'Ämnen, reaktioner & kemi i vardagen',
  tagline: 'Visa eleverna kemin i vardagen.',
  intro: 'Den fjärde volymen innehåller färdiga lektioner och arbetsblad i kemi – från vatten och luft till syror, baser och periodiska systemet.',
  items: ['Ämnen och blandningar', 'Vatten', 'Luft', 'Kemiska reaktioner', 'Syror och baser', 'Periodiska systemet', 'Kemi i vardagen', 'Arbetsblad i 3 nivåer'],
  outro: 'Ett praktiskt stöd för att förklara kemiska samband steg för steg.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 05',
  level: 'Experiment',
  title: 'Experiment & laborationer',
  tagline: 'Låt eleverna undersöka – på ett säkert sätt.',
  intro: 'Den femte volymen samlar 20 säkra experiment med vardagsmaterial, färdiga att genomföra i klassrummet.',
  items: ['20 säkra experiment', 'Vardagsmaterial', 'Labbrapport-mallar', 'Säkerhetsregler', 'Utskrivbara elevblad'],
  outro: 'Experiment som fungerar – utan att du behöver leta på nätet.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 06',
  level: 'Bedömning',
  title: 'Prov, facit & bedömningsmatriser',
  tagline: 'Gör bedömningen enklare och tydligare.',
  intro: 'Den sjätte volymen innehåller prov i två versioner per område, facit och färdigt stöd för bedömning och omdömen.',
  items: ['Prov A/B per område', 'Facit', 'Bedömningsmatriser', 'Självbedömning', '100 färdiga kommentarer till omdömen'],
  outro: 'Mindre tid på rättning – tydligare uppföljning.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];

export const volumesIntro = {
  title: '6 digitala volymer för åk 1–9',
  text: 'Ett komplett NO-paket i biologi, fysik och kemi som hjälper dig att planera och genomföra undervisningen under hela läsåret. Varje volym innehåller 40 sidor med lektioner, arbetsblad, experiment och bedömningsmaterial.'
};

export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lektioner minut för minut',
  text: 'Slipp börja från ett tomt dokument. 12 lektioner på 60 minuter med lärarsida och elevblad – plus färdiga lektioner i varje volym.'
}, {
  icon: FileTextIcon,
  title: 'Arbetsblad i 3 nivåer',
  text: 'Utskrivbara arbetsblad i tre nivåer – så att alla elever kan arbeta med samma innehåll.'
}, {
  icon: FlaskConicalIcon,
  title: '20 säkra experiment',
  text: 'Experiment med vardagsmaterial, labbrapport-mallar och säkerhetsregler – klara att genomföra.'
}, {
  icon: MicroscopeIcon,
  title: 'Biologi, fysik och kemi',
  text: 'Alla tre områdena samlade i ett system – från kroppen och växterna till krafter, energi och kemiska reaktioner.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Prov A/B med facit',
  text: 'Färdiga prov i två versioner, facit, bedömningsmatriser och självbedömning som stöd för rättning och uppföljning.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplanering',
  text: 'Årsplanering för åk 1–3, 4–6 och 7–9, planering vecka för vecka och progressionstabell.'
}];

const pageTexts: Array<{ label: string; title: string; text: string }> = [{
  label: 'Lärarsida – lektion minut för minut',
  title: 'Lektion 3: Flyter eller sjunker? (åk 3)',
  text: 'En hel lektion på 60 minuter planerad steg för steg – från start och genomgång till undersökning och avslutning.'
}, {
  label: 'Elevblad i 3 nivåer',
  title: 'Fotosyntes – hur växter gör mat (åk 4)',
  text: 'Samma innehåll i tre nivåer, så att alla elever kan arbeta med lektionen.'
}, {
  label: 'Årsplanering',
  title: 'Årsplanering åk 4–6',
  text: 'Arbetsområden i biologi, fysik och kemi fördelade över läsåret.'
}, {
  label: 'Prov med facit',
  title: 'Prov åk 7–9 med facit',
  text: 'Färdigt prov och facit (lärarexemplar) för snabb rättning och genomgång.'
}, {
  label: 'Bedömningsmatris',
  title: 'Bedömningsmatris E/C/A',
  text: 'Tydliga kriterier för bedömning – och för F–3: ”på väg / når / fördjupar”.'
}];

export const pageSamples: Array<{ src: string; label: string; title: string; text: string }> = pageTexts.map((page, index) => ({
  ...page,
  src: REAL_PAGE_URLS[index] || basePageSamples[index]?.src || ''
}));

export const pageTypes = ['Lektioner minut för minut', 'Elevblad i 3 nivåer', 'Säkra experiment', 'Prov och facit'];

export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'NO-start på 5 minuter',
  text: '40 korta uppstarter som får igång lektionen direkt.',
  items: ['Gåtor', '”Vad händer om…?”-frågor', 'Bildfrågor', 'Mini-demonstrationer'],
  benefit: 'Starta varje NO-lektion med fokus – utan extra förberedelse.'
}, {
  title: 'Utomhus-NO: 30 färdiga lektioner',
  text: 'Ta med NO-undervisningen ut – med färdiga lektioner för skolgården och naturen.',
  items: ['Naturpromenad', 'Insektsjakt', 'Väderstation', 'Skuggmätning', 'Växtpress', 'Artkort'],
  benefit: 'Mer variation och upplevelser utan extra planering.'
}, {
  title: 'Föräldrakommunikation',
  text: 'Färdiga texter för kontakten med vårdnadshavare under hela läsåret.',
  items: ['Färdiga brev', 'Veckobrev', 'Information om laborationer och friluftsdagar', 'Mall för utvecklingssamtal'],
  benefit: 'Tydlig kommunikation på några minuter.'
}, {
  title: 'Vikariepärm för NO',
  text: '15 lektioner som vikarien kan genomföra utan förberedelse.',
  items: ['15 färdiga lektioner', 'Instruktioner', 'Arbetsblad', 'Facit'],
  benefit: 'Lugn och ro även när du inte kan vara på plats.'
}, {
  title: 'Affischer för NO-salen',
  text: '20 affischer som gör klassrummet till en lärmiljö.',
  items: ['Säkerhetsregler i labbet', 'Periodiska systemet för barn', 'Kroppens organ', 'Vattnets kretslopp', 'Planeterna', 'Näringskedja', 'Ordlista'],
  benefit: 'Skriv ut och sätt upp – direkt.'
}];

export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Öppna NO KLAR™',
  text: 'Ladda ner paketet och öppna volymen du behöver – planering, biologi, fysik, kemi, experiment eller bedömning.'
}, {
  title: 'Välj lektion och material',
  text: 'Hitta rätt arbetsområde och välj lektion, elevblad i rätt nivå eller experiment.'
}, {
  title: 'Förbered dig på 5 minuter',
  text: 'Gå igenom lärarsidan och välj det material du behöver.'
}, {
  title: 'Skriv ut och undervisa',
  text: 'Skriv ut elevbladen eller använd materialet digitalt – och gå in i NO-salen med en tydlig plan.'
}];

export const audienceItems = ['Undervisar i NO i åk 1–3, 4–6 eller 7–9', 'Undervisar i biologi, fysik eller kemi', 'Vill ha ett helt läsårs NO-material organiserat', 'Vill slippa skapa varje arbetsblad från grunden', 'Behöver säkra experiment med enkla material', 'Vill ha arbetsblad i olika nivåer', 'Vill ha färdiga prov, facit och bedömningsmatriser', 'Vill känna sig bättre förberedd inför undervisningen'];

export const timeValueTasks = ['Leta efter säkra experiment', 'Planera laborationer', 'Skriva arbetsblad i olika nivåer', 'Skapa prov för biologi, fysik och kemi', 'Rätta och följa upp elevernas arbete'];

export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitala volymer – 240 sidor',
  detail: 'Årsplanering, biologi, fysik, kemi, experiment och bedömning'
}, {
  label: '5 bonusar utan extra kostnad',
  detail: 'NO-start, utomhus-NO, föräldrakommunikation, vikariepärm och affischer'
}, {
  label: 'För åk 1–9',
  detail: '12 lektioner minut för minut, arbetsblad i 3 nivåer, 20 experiment och prov A/B med facit'
}];

export const offerIncludes = ['Omedelbar digital nedladdning', 'PDF-filer att spara', 'Utskrivbara arbetsblad', 'Ingen fysisk produkt skickas'];

// No buyers yet: only add authentic reviews of NO KLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];

export const faqs = [{
  q: 'Hur får jag materialet?',
  a: 'NO KLAR™ levereras digitalt. Efter köpet får du tillgång till PDF-filer som du kan ladda ner och spara. Du kan använda materialet på dator, surfplatta eller mobil och skriva ut de sidor du behöver.'
}, {
  q: 'Vilka ämnen ingår?',
  a: 'NO KLAR™ täcker biologi, fysik och kemi. Volym 2 är biologi, volym 3 fysik och volym 4 kemi. Dessutom ingår årsplanering med 12 lektioner minut för minut, en volym med experiment och laborationer och en volym med bedömning.'
}, {
  q: 'Passar det för åk 1–9?',
  a: 'Ja. Materialet är skrivet på svenska för NO-undervisning i åk 1–9, med årsplanering för åk 1–3, 4–6 och 7–9. Du väljer material utifrån den årskurs och det område du undervisar i.'
}, {
  q: 'Kan jag verkligen förbereda en lektion på 5 minuter?',
  a: 'NO KLAR™ är utformat för att göra lektionsförberedelserna snabbare. Du kan öppna en volym, välja en färdig lektion eller ett elevblad och gå igenom lärarsidan på ungefär fem minuter. Tiden kan variera beroende på lektionsinnehåll, elevgrupp och om lektionen innehåller ett experiment.'
}, {
  q: 'Är experimenten säkra?',
  a: 'Volym 5 innehåller 20 säkra experiment med vardagsmaterial, tillsammans med säkerhetsregler och labbrapport-mallar. Som alltid ansvarar läraren för att anpassa och leda experimenten utifrån sin elevgrupp.'
}, {
  q: 'Räcker materialet för ett helt läsår?',
  a: 'NO KLAR™ innehåller årsplanering och material som kan användas under hela läsåret. Du väljer, kombinerar och anpassar resurserna efter din planering och elevernas behov.'
}, {
  q: 'Ingår facit?',
  a: 'Ja. Proven finns i version A och B med facit, och paketet innehåller även bedömningsmatriser och självbedömning.'
}, {
  q: 'Kan jag använda materialet för elever på olika nivåer?',
  a: 'Ja. Arbetsbladen finns i tre nivåer, så att du kan ge alla elever uppgifter som passar deras kunskaper och behov.'
}, {
  q: 'Måste jag skriva ut allt?',
  a: 'Nej. Du väljer själv vilka sidor du skriver ut, och du kan även använda delar av materialet digitalt.'
}, {
  q: 'Ingår de fem bonusarna?',
  a: `Ja. Alla fem bonusar ingår i paketet utan extra kostnad – allt för ${PRICE}.`
}, {
  q: 'Är det en prenumeration?',
  a: 'Nej. Du betalar en gång och får tillgång till det digitala materialet. Inga återkommande avgifter.'
}, {
  q: 'Kan jag spara filerna?',
  a: 'Ja. Du kan ladda ner och spara PDF-filerna för fortsatt användning enligt produktens användarvillkor.'
}, {
  q: 'Kan jag använda materialet på mobil eller surfplatta?',
  a: 'Ja. PDF-filerna kan öppnas på dator, surfplatta och mobil. För att granska layouten före utskrift rekommenderar vi dator eller surfplatta.'
}, {
  q: 'Finns det någon garanti?',
  a: 'Ja. NO KLAR™ har 7 dagars nöjdhetsgaranti. Om materialet inte motsvarar dina förväntningar kan du kontakta oss och begära återbetalning inom garantiperioden enligt villkoren.'
}];

export const footerLinks = ['Köpvillkor', 'Integritetspolicy', 'Återbetalningspolicy', 'Kontakt'];
